import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowLeft,
  User,
  Building2,
  MapPin,
  Mail,
  Phone,
  Globe,
  Linkedin,
  Briefcase,
} from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { statusConfig, StatusBadge } from "./StatusBadge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

function MetaRow({ icon: Icon, label, value, href }) {
  if (!value) return null
  return (
    <div className="flex items-start gap-3">
      <Icon size={14} className="text-zinc-400 mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-zinc-400">{label}</p>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-kf-red hover:underline break-all"
          >
            {value}
          </a>
        ) : (
          <p className="text-sm text-zinc-700 break-all">{value}</p>
        )}
      </div>
    </div>
  )
}

export default function CollectiveApplicationShow({ currentUser, application }) {
  const formattedDate = new Date(application.createdAt).toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })

  const handleStatusChange = (newStatus) => {
    router.patch(`/app/admin/collective_applications/${application.id}`, {
      status: newStatus,
    })
  }

  return (
    <AdminLayout currentUser={currentUser}>
      <Head title={`${application.fullName} — Applications — Admin`} />

      <div className="max-w-3xl space-y-6">
        {/* Back link */}
        <Link
          href="/app/admin/submissions"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
        >
          <ArrowLeft size={14} />
          All Submissions
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <StatusBadge status={application.status} />
              <span className="text-xs text-zinc-400">{formattedDate}</span>
            </div>
            <h1 className="text-2xl font-bold text-zinc-900">
              {application.fullName}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              {application.titleRole} at {application.companyName}
            </p>
          </div>

          {/* Headshot */}
          {application.headshotUrl && (
            <a href={application.headshotUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={application.headshotUrl}
                alt={`${application.fullName} headshot`}
                className="w-20 h-20 rounded-full object-cover border border-zinc-200 hover:opacity-90 transition-opacity cursor-zoom-in"
              />
            </a>
          )}
        </div>

        {/* Status update */}
        <Card>
          <CardHeader className="pb-2">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
              Update Status
            </p>
          </CardHeader>
          <CardContent>
            <Select
              value={application.status}
              onValueChange={handleStatusChange}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(statusConfig).map(([value, cfg]) => (
                  <SelectItem key={value} value={value}>
                    {cfg.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Contact & Business info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-1.5">
                <User size={13} className="text-zinc-400" />
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
                  Contact
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <MetaRow icon={Mail} label="Email" value={application.email} href={`mailto:${application.email}`} />
              <MetaRow icon={Phone} label="Phone" value={application.phone} />
              <MetaRow icon={MapPin} label="Location" value={application.location} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-1.5">
                <Building2 size={13} className="text-zinc-400" />
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
                  Business
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <MetaRow icon={Building2} label="Company" value={application.companyName} />
              <MetaRow icon={Briefcase} label="Title / Role" value={application.titleRole} />
              <MetaRow icon={Globe} label="Website" value={application.website} href={application.website} />
              <MetaRow icon={Linkedin} label="LinkedIn" value={application.linkedin} href={application.linkedin} />
            </CardContent>
          </Card>
        </div>

        {/* Faith Reflection */}
        <Card>
          <CardHeader className="pb-2">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
              Faith Reflection
            </p>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">
              {application.faithReflection}
            </p>
          </CardContent>
        </Card>

      </div>
    </AdminLayout>
  )
}
